import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LandingLayout from "./LandingLayout";

const CreatorFaqPage = () => {
  const [activeTab, setActiveTab] = useState("eligibility");

  useEffect(() => {
    const handleScroll = () => {
      const cards = document.querySelectorAll(".faq-card");
      const scrollPos = window.scrollY + 200;

      cards.forEach((card) => {
        const id = card.getAttribute("id");
        if (id) {
          const top = card.offsetTop;
          const height = card.offsetHeight;
          if (scrollPos >= top && scrollPos < top + height) {
            setActiveTab(id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <LandingLayout title="Creator FAQ | Rebel">
      <section className="page-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="breadcrumbs">
            <Link to="/">Home</Link>
            <span>›</span>
            <span>Creator FAQ</span>
          </div>

          <p className="page-label">FAQ</p>
          <h1>Creator Questions</h1>
          <p className="updated">◷ Updated At: 4 months ago</p>
          <p className="hero-description">
            Learn how creator eligibility, monetization, payouts, account verification, visibility, and revenue models work on Rebel.
          </p>
        </div>
        <div className="hero-art d-none">
          <img src="/assets/hero-phones.png" alt="Rebel app shown on two mobile phones" />
        </div>
      </section>

      <div className="faq-layout">
        <aside className="faq-sidebar">
          <div className="sidebar-card">
            <h2>In This Guide</h2>
            <nav className="sidebar-nav">
              <a className={activeTab === "eligibility" ? "active" : ""} href="#eligibility">Eligibility</a>
              <a className={activeTab === "monetization" ? "active" : ""} href="#monetization">Monetization on Rebel</a>
              <a className={activeTab === "subscription-model" ? "active" : ""} href="#subscription-model">Subscription Model</a>
              <a className={activeTab === "ad-revenue" ? "active" : ""} href="#ad-revenue">Ad Revenue Model</a>
              <a className={activeTab === "start-earning" ? "active" : ""} href="#start-earning">How Do I Start Earning?</a>
              <a className={activeTab === "payouts" ? "active" : ""} href="#payouts">Payouts</a>
              <a className={activeTab === "how-much" ? "active" : ""} href="#how-much">How Much Will I Receive?</a>
              <a className={activeTab === "paid-vs-free" ? "active" : ""} href="#paid-vs-free">Paid Account VS Free Account</a>
              <a className={activeTab === "fundme" ? "active" : ""} href="#fundme">FundMe Button</a>
              <a className={activeTab === "approval" ? "active" : ""} href="#approval">Account Approval</a>
              <a className={activeTab === "payout-rejected" ? "active" : ""} href="#payout-rejected">Payout Request Rejected</a>
              <a className={activeTab === "payout-not-received" ? "active" : ""} href="#payout-not-received">Payout Not Received</a>
              <a className={activeTab === "page-visibility" ? "active" : ""} href="#page-visibility">Who Can See My Page?</a>
              <a className={activeTab === "why-rebel" ? "active" : ""} href="#why-rebel">Why Rebel?</a>
            </nav>


            <div className="support-card">
              <h3>Still have questions?</h3>
              <p>Our creator support team is here to help.</p>
              <a href="mailto:support@rebeltv.me" className="support-button">Contact Support</a>
            </div>
          </div>
        </aside>

        <section className="faq-content">
          <article className="faq-card" id="eligibility">
            <div className="faq-icon">
              <img src="/assets/icons/eligibility.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Eligibility</h2>
              <h3>Can I Become a Creator From My Country?</h3>
              <p>At this time, Creators must be based in The Bahamas.</p>
              <p>Your subscriber base, however, can come from anywhere in the world.</p>
            </div>
          </article>

          <article className="faq-card" id="monetization">
            <div className="faq-icon">
              <img src="/assets/icons/monetization.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Monetization on Rebel</h2>
              <p>Rebel offers two ways to earn income:</p>
              <ul>
                <li>Subscription Model (Premium Content)</li>
                <li>Ad Revenue Model (Paid Per View)</li>
              </ul>
              <p className="highlight-note">Creators must choose one model per account.</p>
            </div>
          </article>

          <article className="faq-card" id="subscription-model">
            <div className="faq-icon">
              <img src="/assets/icons/subscription.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Subscription Model (Premium)</h2>
              <p>Set a monthly subscription price between <strong>$5 – $50</strong>.</p>
              <p>Subscribers pay monthly to access your content.</p>
              <h3>Earnings</h3>
              <p>Creators receive <strong>80%</strong> of all subscription revenue, including:</p>
              <ul>
                <li>Monthly subscriptions</li>
                <li>Paid posts</li>
                <li>Live streams</li>
                <li>Funding through the FundMe button</li>
              </ul>
              <p>Rebel retains <strong>20%</strong> to cover:</p>
              <ul>
                <li>Payment processing</li>
                <li>Hosting and streaming</li>
                <li>Support services</li>
                <li>Platform infrastructure</li>
              </ul>

              <div className="important-box">
                <strong>Important</strong>
                <ul className="status-list">
                  <li className="negative">No ads will run on your videos.</li>
                  <li className="negative">You are not eligible for ad revenue.</li>
                  <li className="positive">Your content remains premium and ad-free.</li>
                </ul>
                <p>This protects your paying subscribers’ experience.</p>
              </div>
            </div>
          </article>

          <article className="faq-card" id="ad-revenue">
            <div className="faq-icon">
              <img src="/assets/icons/ads.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Ad Revenue Model (Paid Per View)</h2>
              <p>If your account is set to Free, your videos may qualify for pre-roll ads.</p>
              <p>Rebel runs a short advertisement before your video.</p>
              <p>Creators earn a share of advertising revenue generated from ads shown before their videos.</p>

              <h3>Revenue Split</h3>
              <div className="revenue-split">
                <div><strong>60%</strong><span>To Creator</span></div>
                <div><strong>40%</strong><span>To Rebel</span></div>
              </div>

              <h3>Estimated Earnings</h3>
              <p>Creator earnings from advertising vary depending on advertiser demand, but creators may earn approximately:</p>
              <p className="earnings-highlight">~$4 – $6 per 1,000 ad views</p>

              <div className="important-box">
                <strong>Important</strong>
                <ul>
                  <li>Ads only run on Free accounts.</li>
                  <li>Subscription accounts are not eligible.</li>
                  <li>No ads will appear on paid content.</li>
                </ul>
                <p>This model is ideal for:</p>
                <ul>
                  <li>High-volume creators</li>
                  <li>Viral content</li>
                  <li>Entertainment and cultural content</li>
                  <li>Public-facing shows</li>
                </ul>
              </div>
            </div>
          </article>

          <article className="faq-card" id="start-earning">
            <div className="faq-icon">
              <img src="/assets/icons/start-earning.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>How Do I Start Earning?</h2>
              <p>To activate monetization:</p>
              <ul>
                <li>Add your bank account. It must be in your name.</li>
                <li>The bank must match the country of your verified ID.</li>
                <li>Upload your Photo ID.</li>
                <li>Complete account verification.</li>
                <li>Choose your monetization model.</li>
                <li>Set your subscription price if using the Subscription Model.</li>
              </ul>
              <p className="highlight-note">You may not be able to set pricing until verification is complete.</p>
            </div>
          </article>

          <article className="faq-card" id="payouts">
            <div className="faq-icon">
              <img src="/assets/icons/payouts.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Payouts</h2>
              <ul>
                <li>Minimum withdrawal: $10</li>
                <li>Earnings become available on a 7-day rolling basis</li>
                <li>Withdrawals are processed immediately</li>
                <li>Funds appear in 3–5 business days</li>
                <li>Payouts are sent in BSD</li>
              </ul>
              <p>Your wallet displays:</p>
              <ul>
                <li>Total Balance</li>
                <li>Current Balance</li>
                <li>Earnings</li>
              </ul>
              <p>If a refund or chargeback occurs, adjustments will appear in your wallet.</p>
            </div>
          </article>

          <article className="faq-card" id="how-much">
            <div className="faq-icon">
              <img src="/assets/icons/amount.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>How Much Will I Receive?</h2>
              <p>The amount shown in your Current Balance is the exact BSD amount sent to your bank.</p>
              <p>Your bank may apply currency conversion or transfer fees.</p>
            </div>
          </article>

          <article className="faq-card" id="paid-vs-free">
            <div className="faq-icon">
              <img src="/assets/icons/paid-vs-free.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Paid Account VS Free Account</h2>
              <div className="comparison-grid">
                <div className="comparison-card">
                  <h3>Paid Subscription Account</h3>
                  <ul>
                    <li>Fans must pay monthly</li>
                    <li>Content is hidden from non-subscribers</li>
                    <li>No advertising</li>
                    <li>80% revenue share</li>
                  </ul>
                  <strong>Best for:</strong>
                  <ul>
                    <li>Premium creators</li>
                    <li>Exclusive content</li>
                    <li>Strong fan communities</li>
                  </ul>
                </div>

                <div className="comparison-card">
                  <h3>Free Account</h3>
                  <ul>
                    <li>Anyone registered can subscribe for free</li>
                    <li>Ads may run before videos</li>
                    <li>60% ad revenue share</li>
                    <li>Paid posts and live streams are still allowed</li>
                  </ul>
                  <strong>Best for:</strong>
                  <ul>
                    <li>Growing audiences</li>
                    <li>Viral content</li>
                    <li>Ad-supported shows</li>
                  </ul>
                </div>
              </div>
            </div>
          </article>

          <article className="faq-card" id="fundme">
            <div className="faq-icon">
              <img src="/assets/icons/fundme.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>FundMe Button</h2>
              <p>To enable FundMe:</p>
              <ol>
                <li>Add your bank account.</li>
                <li>Upload verification documents.</li>
                <li>Complete verification.</li>
                <li>Post new content.</li>
              </ol>
              <p>Once verified, FundMe appears under your posts.</p>
            </div>
          </article>

          <article className="faq-card" id="approval">
            <div className="faq-icon">
              <img src="/assets/icons/approval.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Account Approval Requirements</h2>
              <p>Submit a clear photo of your government-issued ID.</p>
              <h3>Requirements</h3>
              <ul>
                <li>Full colour</li>
                <li>Not edited</li>
                <li>All four corners visible</li>
                <li>Not expired</li>
                <li>Under 7MB</li>
                <li>.PNG or .JPG format</li>
              </ul>
              <h3>Applications may be rejected for:</h3>
              <ul>
                <li>Poor image quality</li>
                <li>Expired ID</li>
                <li>Misleading profile</li>
                <li>Prohibited services</li>
                <li>Applicants under 18</li>
              </ul>
            </div>
          </article>

          <article className="faq-card" id="payout-rejected">
            <div className="faq-icon">
              <img src="/assets/icons/rejected.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Payout Request Rejected</h2>
              <p>This is usually due to:</p>
              <ul>
                <li>Expired ID</li>
                <li>Missing payout information</li>
                <li>Invalid bank details</li>
                <li>Unsupported payout jurisdiction</li>
              </ul>
            </div>
          </article>

          <article className="faq-card" id="payout-not-received">
            <div className="faq-icon">
              <img src="/assets/icons/not-received.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Payout Not Received</h2>
              <p>Funds typically arrive within 3–5 business days.</p>
              <p>In rare cases, processing may take up to 10 business days.</p>
              <p>
                If more than 10 days have passed, email: <a href="mailto:payments@rebeltv.me">payments@rebeltv.me</a>
              </p>
            </div>
          </article>

          <article className="faq-card" id="page-visibility">
            <div className="faq-icon">
              <img src="/assets/icons/visibility.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Who Can See My Page?</h2>
              <p>Your public profile displays:</p>
              <ul>
                <li>Profile name</li>
                <li>@username</li>
                <li>Avatar and cover image</li>
                <li>Subscriber count</li>
              </ul>
              <p>Content visibility depends on your monetization model.</p>
            </div>
          </article>

          <article className="faq-card" id="why-rebel">
            <div className="faq-icon">
              <img src="/assets/icons/why-rebel.svg" alt="" />
            </div>
            <div className="faq-copy">
              <h2>Why Rebel?</h2>
              <p>Rebel gives creators flexibility:</p>
              <ul>
                <li>Build a premium subscription business</li>
                <li>Or earn from advertising views</li>
              </ul>
              <p className="closing-statement">You choose how you grow.</p>
            </div>
          </article>
        </section>
      </div>
    </LandingLayout>
  );
};

export default CreatorFaqPage;
